/**
 * THE DASHBOARD ACCENT PALETTE.
 *
 * The screens were built from two browns, a cream, and one orange. That is the
 * right recipe for a marketing PAGE — warm, quiet, brand-forward — but applied
 * to product UI it produces the problem: every avatar, every icon tile and
 * every category reads as the same dark smudge, so nothing is distinguishable
 * and the whole thing looks flat and dated.
 *
 * The four colours previously used for avatars (#7C2D12, #1E3A5F, #134E4A,
 * #3F3A2E) all sit at roughly 20-25% lightness — near-black at avatar size.
 * Four near-blacks is not a palette, it is one colour with rounding error.
 *
 * These are mid-value (~40-50% lightness), so they are genuinely different
 * from each other AND still carry white text. Every hue is pulled slightly
 * warm so it belongs on cream rather than fighting it — this is a warmer,
 * more saturated set, not a cool material palette dropped in.
 *
 * RULES:
 * - `solid` is for filled objects: avatars, group chips, status dots.
 * - `tint` + `ink` are for icon tiles: a light wash with a saturated glyph.
 *   Never a dark ink tile with a white glyph — a column of those was what made
 *   the lists read as a row of identical black squares.
 * - Orange (`clay`) stays reserved for the accent: primary actions, live
 *   states, the one thing on a screen that matters most. Do not spend it on a
 *   category tile, or it stops meaning anything.
 */
export type Hue = {
  solid: string;
  tint: string;
  ink: string;
};

export const HUES: Record<string, Hue> = {
  clay: {
    solid: '#C2410C',
    tint: '#FEEDE1',
    ink: '#C2410C'
  },
  teal: {
    solid: '#0F766E',
    tint: '#DEF2EF',
    ink: '#0F766E'
  },
  indigo: {
    solid: '#4159A6',
    tint: '#E8EBFA',
    ink: '#4159A6'
  },
  plum: {
    solid: '#8B3A6B',
    tint: '#FAE7F1',
    ink: '#8B3A6B'
  },
  amber: {
    solid: '#A16207',
    tint: '#FBF0D9',
    ink: '#8A5406'
  },
  sky: {
    solid: '#1C6FA8',
    tint: '#E1EFF8',
    ink: '#1C6FA8'
  },
  moss: {
    solid: '#4D7C2A',
    tint: '#ECF4E2',
    ink: '#41691F'
  }
};

/**
 * The avatar rotation. Ordered so that adjacent people in a stack never land
 * on neighbouring hues — an avatar stack is the densest place colour appears,
 * so it is where near-identical values show up worst.
 */
export const AVATAR_HUES = [HUES.indigo.solid, HUES.clay.solid, HUES.teal.solid, HUES.plum.solid, HUES.sky.solid, HUES.amber.solid, HUES.moss.solid];

/** Deterministic hue for a person, so the same initials keep the same colour
 * across every screen in the set. */
export function avatarHue(initials: string): string {
  let n = 0;
  for (let i = 0; i < initials.length; i++) n = (n * 31 + initials.charCodeAt(i)) % 997;
  return AVATAR_HUES[n % AVATAR_HUES.length];
}
